import { Bar } from "recharts";
import type { TooltipProps } from "recharts";
import { Chart, ChartTypes } from "@uicore";
import { useAppState } from "@/modules/app";
import type { AutoTuneSavingsStackedPoint } from "../types";
import { formatChartCurrency, formatChartDate, type Granularity } from "./chartFormatters";
import AutoTuneSavingsTooltip from "./AutoTuneSavingsTooltip";
import EmptyChart from "./EmptyChart";

// Colors mirror the Snowflake warehouse auto-manage graph so the two read
// the same: spend amber, possible-savings gold, auto-tune-savings green.
const SPEND_COLOR = "#FFCE73";
const POTENTIAL_COLOR = "#DB9717";
const REALIZED_COLOR = "#01CD8C";

interface Props {
  data: AutoTuneSavingsStackedPoint[];
  granularity: Granularity;
  height?: number;
  // Omit the green "Auto Tune Savings" (realized) bar and its legend
  // entry. Defaults to false (jobs — show realized on top). Clusters set
  // this: there is no realized-savings source, so only Spend + Possible
  // Savings render.
  hideRealized?: boolean;
}

// Single stacked daily chart mirroring the Snowflake warehouse graph.
// Stacked bottom -> top: spend (run cost), potential savings, then
// realized (auto-tune) savings on top. Realized/potential carry their
// DBU/infra split in the tooltip. Legend click isolates a segment.
const AutoTuneSavingsChart = ({ data, granularity, height = 360, hideRealized = false }: Props) => {
  const { currency } = useAppState();

  if (!data.length) {
    return <EmptyChart height={height} />;
  }

  // When realized is shown it sits on top (rounded cap); otherwise the
  // potential bar is the topmost segment and takes the rounded cap.
  const potentialIsTop = hideRealized;

  const renderBars = (selectedLegend?: string) => (
    <>
      <Bar
        key="spend"
        dataKey="spend"
        name="Spend"
        fill={SPEND_COLOR}
        stackId="cost"
        radius={[0, 0, 0, 0]}
        hide={Boolean(selectedLegend && selectedLegend !== "spend")}
      />
      <Bar
        key="potential"
        dataKey="potential"
        name="Possible Savings"
        fill={POTENTIAL_COLOR}
        stackId="cost"
        radius={potentialIsTop ? [4, 4, 0, 0] : [0, 0, 0, 0]}
        hide={Boolean(selectedLegend && selectedLegend !== "potential")}
      />
      {!hideRealized && (
        <Bar
          key="realized"
          dataKey="realized"
          name="Auto Tune Savings"
          fill={REALIZED_COLOR}
          stackId="cost"
          radius={[4, 4, 0, 0]}
          hide={Boolean(selectedLegend && selectedLegend !== "realized")}
        />
      )}
    </>
  );

  return (
    <div style={{ width: "100%", height: height + 32, paddingBottom: 32 }}>
      <Chart
        title=""
        type={ChartTypes.BarChart}
        xAxisDataKey="period"
        xAxisLabelFormatter={value => formatChartDate(value, granularity)}
        yAxisLabelFormatter={value => formatChartCurrency(value, currency)}
        data={data}
        height={height}
        tooltipProps={{
          content: (props: TooltipProps<number, string>) => (
            <AutoTuneSavingsTooltip {...props} granularity={granularity} currency={currency} />
          ),
        }}
      >
        {({ selectedLegend }) => renderBars(selectedLegend)}
      </Chart>
    </div>
  );
};

export default AutoTuneSavingsChart;
